/**
 * Stabilize spot detections across frames (reduce flicker + hand shake).
 * trackOverlaySpots: once confirmed, keep following across misses.
 */

function spotCenter(spot) {
  if (spot.nx != null) {
    return {
      x: spot.nx + spot.nw / 2,
      y: spot.ny + spot.nh / 2,
    };
  }
  return {
    x: (spot.left + spot.width / 2) / 100,
    y: (spot.top + spot.height / 2) / 100,
  };
}

function dist(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function averageSpot(members) {
  const n = members.length;
  if (!n) return null;
  if (n === 1) return { ...members[0] };

  const nx = members.reduce((s, m) => s + m.nx, 0) / n;
  const ny = members.reduce((s, m) => s + m.ny, 0) / n;
  const nw = members.reduce((s, m) => s + m.nw, 0) / n;
  const nh = members.reduce((s, m) => s + m.nh, 0) / n;
  const diameterMm =
    members.reduce((s, m) => s + (Number(m.diameterMm) || 0), 0) / n;
  const best = members
    .slice()
    .sort((a, b) => (b.diameterMm || 0) - (a.diameterMm || 0))[0];

  return {
    ...best,
    nx,
    ny,
    nw,
    nh,
    diameterMm: Number(diameterMm.toFixed(2)),
    left: members.reduce((s, m) => s + (m.left || 0), 0) / n,
    top: members.reduce((s, m) => s + (m.top || 0), 0) / n,
    width: members.reduce((s, m) => s + (m.width || 0), 0) / n,
    height: members.reduce((s, m) => s + (m.height || 0), 0) / n,
  };
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function blendSpot(prev, next, alpha = 0.35) {
  if (!prev) return { ...next };
  if (!next) return { ...prev };
  return {
    ...next,
    nx: lerp(prev.nx, next.nx, alpha),
    ny: lerp(prev.ny, next.ny, alpha),
    nw: lerp(prev.nw, next.nw, alpha),
    nh: lerp(prev.nh, next.nh, alpha),
    diameterMm: Number(
      lerp(Number(prev.diameterMm) || 0, Number(next.diameterMm) || 0, alpha).toFixed(2)
    ),
    left: lerp(prev.left || 0, next.left || 0, alpha),
    top: lerp(prev.top || 0, next.top || 0, alpha),
    width: lerp(prev.width || 0, next.width || 0, alpha),
    height: lerp(prev.height || 0, next.height || 0, alpha),
    oversized: Boolean(next.oversized ?? prev.oversized),
    isKnot: Boolean(next.isKnot ?? prev.isKnot),
    label: next.label || prev.label,
  };
}

/**
 * Match current spots to tracks; emit only stable ones (smoothed).
 */
export function stabilizeOverlaySpots(history, minHits = 3, matchRadius = 0.09) {
  if (!history?.length) return [];

  const recent = history.slice(-5);
  const latest = recent[recent.length - 1] || [];
  if (!latest.length) return [];

  const stable = [];

  for (const spot of latest) {
    const c = spotCenter(spot);
    const members = [spot];

    for (let i = 0; i < recent.length - 1; i += 1) {
      const frame = recent[i] || [];
      let best = null;
      let bestD = matchRadius;
      for (const other of frame) {
        const d = dist(c, spotCenter(other));
        if (d <= bestD) {
          bestD = d;
          best = other;
        }
      }
      if (best) members.push(best);
    }

    if (members.length >= Math.min(minHits, recent.length)) {
      stable.push(averageSpot(members));
    }
  }

  return stable;
}

/**
 * Persistent tracker: once confirmed, keep following across a few missed frames.
 * Never keep two tracks on the same physical spot.
 */
export function trackOverlaySpots(
  tracks,
  detections,
  {
    matchRadius = 0.1,
    confirmHits = 2,
    maxMisses = 6,
    smooth = 0.4,
    /** ห้าม track จุดเดียวกันซ้ำ — ระยะรวม track ที่ใกล้กัน */
    dedupeRadius = 0.08,
  } = {}
) {
  const list = Array.isArray(tracks) ? tracks : [];
  const dets = Array.isArray(detections) ? detections : [];
  const usedDet = new Set();

  for (const track of list) {
    const c = spotCenter(track.spot);
    let bestIdx = -1;
    let bestD = matchRadius;
    for (let i = 0; i < dets.length; i += 1) {
      if (usedDet.has(i)) continue;
      const d = dist(c, spotCenter(dets[i]));
      if (d <= bestD) {
        bestD = d;
        bestIdx = i;
      }
    }

    if (bestIdx >= 0) {
      usedDet.add(bestIdx);
      track.spot = blendSpot(track.spot, dets[bestIdx], smooth);
      track.hits += 1;
      track.misses = 0;
      track.confirmed = track.confirmed || track.hits >= confirmHits;
    } else {
      track.misses += 1;
    }
    track.age += 1;
  }

  // New tracks only if not near an existing track (ห้ามจุดเดียวกัน)
  dets.forEach((det, i) => {
    if (usedDet.has(i)) return;
    const c = spotCenter(det);
    const nearExisting = list.some(
      (t) => dist(c, spotCenter(t.spot)) <= dedupeRadius
    );
    if (nearExisting) return;
    list.push({
      id: `trk-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 6)}`,
      spot: { ...det },
      hits: 1,
      misses: 0,
      age: 1,
      confirmed: false,
    });
  });

  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (list[i].misses > maxMisses) list.splice(i, 1);
  }

  // Merge duplicate tracks that drifted onto the same spot
  list.sort((a, b) => b.hits - a.hits || b.age - a.age);
  for (let i = 0; i < list.length; i += 1) {
    const a = list[i];
    if (!a) continue;
    const ca = spotCenter(a.spot);
    for (let j = list.length - 1; j > i; j -= 1) {
      const b = list[j];
      if (dist(ca, spotCenter(b.spot)) <= dedupeRadius) {
        // keep stronger track; absorb hits
        a.hits = Math.max(a.hits, b.hits) + 1;
        a.confirmed = a.confirmed || b.confirmed || a.hits >= confirmHits;
        a.misses = Math.min(a.misses, b.misses);
        a.spot = blendSpot(a.spot, b.spot, 0.5);
        list.splice(j, 1);
      }
    }
  }

  return list
    .filter((t) => t.confirmed || t.hits >= confirmHits)
    .map((t) => ({
      ...t.spot,
      id: t.id,
      trackHits: t.hits,
    }));
}

/**
 * Consensus for final scan: merge spots across frames (handheld-friendly radius).
 */
export function consensusSpots(frameSpotLists, matchRadius = 0.1) {
  if (!frameSpotLists?.length) return [];

  const clusters = [];

  frameSpotLists.forEach((spots) => {
    (spots || []).forEach((spot) => {
      const c = spotCenter(spot);
      let cluster = clusters.find((cl) => dist(cl.center, c) <= matchRadius);
      if (!cluster) {
        cluster = { center: c, members: [], hits: 0 };
        clusters.push(cluster);
      }
      cluster.members.push(spot);
      cluster.hits += 1;
      const n = cluster.members.length;
      cluster.center = {
        x: cluster.members.reduce((s, m) => s + spotCenter(m).x, 0) / n,
        y: cluster.members.reduce((s, m) => s + spotCenter(m).y, 0) / n,
      };
    });
  });

  // ต้องเจอซ้ำอย่างน้อยครึ่งหนึ่งของเฟรม (ขั้นต่ำ 2) — ให้ใกล้เคียง live preview
  // เดิมใช้ 60% → ถ่าย 4 เฟรมต้อง 3 ครั้ง ทำให้จุดที่เห็นบนจอหลุดตอนบันทึกได้ง่าย
  const need = Math.max(2, Math.ceil(frameSpotLists.length * 0.5));
  return clusters
    .filter((cl) => cl.hits >= need)
    .map((cl) => averageSpot(cl.members) || cl.members[0]);
}
